// FILE: packages/components/src/admin/sprints/DayEditorPanel.tsx

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable, DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar, CheckSquare, Edit3, List, Send, GripVertical, Plus, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';

import { Button, IconButton, TextareaField, TextField, Checkbox } from '@dopomogai/ui';
import { DraggableTaskCard } from '../tasks/DraggableTaskCard';
import type { SprintDay, ProjectTask, DayContentUpdatePayload, ChecklistItem } from '@dopomogai/types';
import { useUiStore } from '../../../../../../apps/website/src/stores/ui';

// ========================================================================
// ==                      INTERNAL SUB-COMPONENTS                       ==
// ========================================================================

// --- Internal Day Tab Component ---
const DayTab: React.FC<{ day: SprintDay; isSelected: boolean; onSelect: () => void; }> = ({ day, isSelected, onSelect }) => {
    const dayDate = new Date(day.date);
    const dayOfWeek = dayDate.toLocaleDateString(undefined, { weekday: 'short' });
    const dayOfMonth = dayDate.getDate();
    return (
      <button
        onClick={onSelect}
        className={clsx('p-3 w-20 text-center rounded-lg border-2 flex-shrink-0 transition-all duration-150', isSelected ? 'bg-white border-black shadow-[2px_2px_0_0_black]' : 'bg-slate-100 border-slate-300 hover:border-slate-400')}
        aria-current={isSelected ? 'true' : 'false'}
      >
        <span className="block text-xs font-semibold text-slate-500">{dayOfWeek}</span>
        <span className="block text-2xl font-bold text-slate-800 mt-1">{dayOfMonth}</span>
      </button>
    );
};

// --- Internal Checklist Editor Component ---
const ChecklistEditor: React.FC<{ items: ChecklistItem[]; onItemsChange: (newItems: ChecklistItem[]) => void; }> = ({ items, onItemsChange }) => {
    const { t } = useTranslation('admin_sprints');
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const SortableItem: React.FC<{ item: ChecklistItem }> = ({ item }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
        const style = { transform: CSS.Transform.toString(transform), transition };

        const handleItemChange = (change: Partial<ChecklistItem>) => {
            onItemsChange(items.map(i => i.id === item.id ? { ...i, ...change } : i));
        };

        const handleRemove = () => onItemsChange(items.filter(i => i.id !== item.id));

        return (
            <li ref={setNodeRef} style={style} className={clsx("flex items-center gap-2 p-2 rounded-lg bg-white", isDragging && "shadow-lg ring-2 ring-emerald-500 z-10")}>
                <button {...attributes} {...listeners} className="cursor-grab p-1 text-slate-400 focus:outline-none focus-visible:ring-2 rounded"><GripVertical size={18} /></button>
                <Checkbox id={`check-${item.id}`} checked={item.is_checked} onCheckedChange={(checked) => handleItemChange({ is_checked: !!checked })} />
                <TextField name={`text-${item.id}`} value={item.text} onChange={(e) => handleItemChange({ text: e.target.value })} className="flex-grow" containerClassName="w-full" />
                <IconButton icon={<Trash2 size={16} />} variant="ghost" onClick={handleRemove} />
            </li>
        );
    };
    
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
          const oldIndex = items.findIndex(item => item.id === active.id);
          const newIndex = items.findIndex(item => item.id === over.id);
          onItemsChange(arrayMove(items, oldIndex, newIndex));
        }
    };
    
    const handleAddItem = () => onItemsChange([...items, { id: uuidv4(), text: '', is_checked: false }]);

    return (
        <div className="p-4 bg-slate-50 border-2 border-dashed rounded-xl">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    <ul className="space-y-2">
                        {items.map(item => <SortableItem key={item.id} item={item} />)}
                    </ul>
                </SortableContext>
            </DndContext>
            <div className="mt-3"><Button textKey="dayEditor.checklist.addItem" variant="secondary" icon={<Plus size={16} />} onClick={handleAddItem} /></div>
        </div>
    );
};

// ========================================================================
// ==                       MAIN EXPORTED COMPONENT                      ==
// ========================================================================

interface DayEditorPanelProps {
  sprintDays: SprintDay[];
  currentDay: SprintDay | null;
  tasksForDay: ProjectTask[];
  onSelectDay: (dayId: string) => void;
  onUpdateDayContent: (dayId: string, payload: DayContentUpdatePayload) => Promise<void>;
  onPublishDailyLog?: (dayId: string, logContent: string) => Promise<void>;
}

export const DayEditorPanel: React.FC<DayEditorPanelProps> = ({ sprintDays, currentDay, tasksForDay, onSelectDay, onUpdateDayContent, onPublishDailyLog }) => {
  const { t } = useTranslation(['admin_sprints', 'admin_tasks']);
  // --- FIX: Use the new function from the refactored store ---
  const { openTaskInspector } = useUiStore();
  const { setNodeRef, isOver } = useDroppable({ id: 'day-drop-zone', data: { dayId: currentDay?.id }, disabled: !currentDay });

  // --- DRAFT STATE FOR ALL EDITABLE FIELDS ---
  const [draftTitle, setDraftTitle] = useState('');
  const [draftSummary, setDraftSummary] = useState('');
  const [draftChecklist, setDraftChecklist] = useState<ChecklistItem[]>([]);
  const [draftLog, setDraftLog] = useState('');

  useEffect(() => {
    setDraftTitle(currentDay?.public_title || '');
    setDraftSummary(currentDay?.summary || '');
    setDraftChecklist(currentDay?.checklist || []);
    setDraftLog(''); // Always reset log on day change
  }, [currentDay]);

  const handleContentSave = () => {
    if (!currentDay) return;
    const payload: DayContentUpdatePayload = {
        checklist: draftChecklist,
        translations: { 'en': { public_title: draftTitle, summary: draftSummary } }
    };
    onUpdateDayContent(currentDay.id, payload);
  };
  
  const handleLogPublish = () => {
    if (!currentDay || !onPublishDailyLog) return;
    onPublishDailyLog(currentDay.id, draftLog);
  };

  return (
    <div className="flex-grow h-full flex flex-col bg-white">
      <header className="p-3 border-b-2 border-black flex-shrink-0">
        <div className="flex items-center gap-3 overflow-x-auto pb-2">
            <Calendar size={20} className="text-slate-400 mx-2 flex-shrink-0" />
            {sprintDays.map(day => ( <DayTab key={day.id} day={day} isSelected={currentDay?.id === day.id} onSelect={() => onSelectDay(day.id)} /> ))}
        </div>
      </header>
      
      <AnimatePresence mode="wait">
        <motion.div
            key={currentDay?.id || 'empty'}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="flex-grow flex flex-col overflow-y-auto"
        >
        {!currentDay ? (
            <div className="flex-grow flex items-center justify-center text-slate-500 text-center p-8">
                <div>
                    <h3 className="text-xl font-bold">{t('dayEditor.selectDayTitle')}</h3>
                    <p className="mt-2">{t('dayEditor.selectDayDescription')}</p>
                </div>
            </div>
        ) : (
            <div className="flex-grow p-5 space-y-8">
                <section>
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Edit3 size={20} /> {t('dayEditor.contentTitle')}</h3>
                        <Button textKey="common:saveChanges" onClick={handleContentSave} size="sm" />
                    </div>
                    <div className="space-y-4">
                        <TextField name="day-title" label={t('dayEditor.publicTitleLabel')} value={draftTitle} onChange={e => setDraftTitle(e.target.value)} />
                        <TextareaField name="day-summary" label={t('dayEditor.summaryLabel')} value={draftSummary} onChange={e => setDraftSummary(e.target.value)} rows={3} />
                        <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-2"><CheckSquare size={16}/> {t('dayEditor.checklist.title')}</h4>
                            <ChecklistEditor items={draftChecklist} onItemsChange={setDraftChecklist} />
                        </div>
                    </div>
                </section>
                
                <section>
                     <h3 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2"><List size={20} /> {t('dayEditor.tasksTitle')} ({tasksForDay.length})</h3>
                    <div ref={setNodeRef} className={clsx("p-3 space-y-3 min-h-[120px] rounded-xl border-2", isOver ? "border-sky-500 bg-sky-50" : "border-dashed")}>
                        {tasksForDay.length > 0 ? (
                            tasksForDay.map(task => ( <DraggableTaskCard key={task._id} task={task} onExpand={() => openTaskInspector(task, { projectId: task.project_id, sprintId: task.sprint_id ?? undefined })} /> ))
                        ) : (
                            <div className="text-center text-sm text-slate-500 py-8">{t('dayEditor.dropTasksHere')}</div>
                        )}
                    </div>
                </section>

                <section>
                    <h3 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2"><Send size={20} /> {t('dayEditor.dailyLogTitle')}</h3>
                     <div className="p-4 bg-emerald-50 border-2 border-emerald-200 rounded-xl space-y-3">
                        <TextareaField name="daily-log" label={t('dayEditor.dailyLogLabel')} value={draftLog} onChange={e => setDraftLog(e.target.value)} rows={5} placeholder={t('dayEditor.dailyLogPlaceholder')} />
                        <div className="flex justify-end">
                            <Button textKey="dayEditor.publishButton" disabled={!draftLog.trim()} onClick={handleLogPublish} />
                        </div>
                     </div>
                </section>
            </div>
        )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};